// src/components/AltaSocio.jsx
import { useState } from "react";
import { authFetch } from "../services/auth";

export default function AltaSocio({ onCancel, onComplete }) {
  const [paso, setPaso] = useState(1);

  // Estados de los tres pasos
  const [datos, setDatos] = useState({
    nombre: "",
    apellidos: "",
    dni: "",
    email: "",
    telefono: "",
    direccion: "",
    dniFile: null,
    dniCaducidad: "",
  });

  const [docsPaso2, setDocsPaso2] = useState({
    capFile: null,
    capCaducidad: "",
    permisoFile: null,
    permisoCaducidad: "",
    tarjetaFile: null,
    tarjetaCaducidad: "",
  });

  const [datosBancarios, setDatosBancarios] = useState({
    iban: "",
    justificanteFile: null,
  });

  // Control de errores y envío
  const [errores, setErrores] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ----- Handlers de inputs -----
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setDatos((prev) => ({ ...prev, [name]: files ? files[0] : value }));
  };

  const handlePaso2Change = (e) => {
    const { name, value, files } = e.target;
    setDocsPaso2((prev) => ({ ...prev, [name]: files ? files[0] : value }));
  };

  const handlePaso3Change = (e) => {
    const { name, value, files } = e.target;
    setDatosBancarios((prev) => ({ ...prev, [name]: files ? files[0] : value }));
  };

  // ----- Validaciones por paso -----
  const validarPaso1 = () => {
    const errs = {};
    if (!datos.nombre) errs.nombre = "Requerido";
    if (!datos.apellidos) errs.apellidos = "Requerido";
    if (!datos.dni) errs.dni = "Requerido";
    if (!datos.email) errs.email = "Requerido";
    if (!datos.telefono) errs.telefono = "Requerido";
    if (!datos.direccion) errs.direccion = "Requerido";
    if (!datos.dniFile) errs.dniFile = "Falta el archivo del DNI";
    if (!datos.dniCaducidad) errs.dniCaducidad = "Falta la fecha";
    setErrores(errs);
    return Object.keys(errs).length === 0;
  };

  const validarPaso2 = () => {
    const errs = {};
    if (!docsPaso2.capFile) errs.capFile = "Falta el archivo CAP";
    if (!docsPaso2.capCaducidad) errs.capCaducidad = "Falta fecha CAP";
    if (!docsPaso2.permisoFile) errs.permisoFile = "Falta el permiso";
    if (!docsPaso2.permisoCaducidad) errs.permisoCaducidad = "Falta fecha permiso";
    if (!docsPaso2.tarjetaFile) errs.tarjetaFile = "Falta la tarjeta sanitaria";
    if (!docsPaso2.tarjetaCaducidad) errs.tarjetaCaducidad = "Falta fecha tarjeta";
    setErrores(errs);
    return Object.keys(errs).length === 0;
  };

  const validarPaso3 = () => {
    const errs = {};
    if (!datosBancarios.iban) errs.iban = "Falta el número de cuenta";
    if (!datosBancarios.justificanteFile)
      errs.justificanteFile = "Falta el justificante bancario";
    setErrores(errs);
    return Object.keys(errs).length === 0;
  };

  // ----- Navegación entre pasos -----
  const siguiente = () => {
    if (paso === 1 && !validarPaso1()) return;
    setErrores({});
    setPaso((p) => p + 1);
  };

  const anterior = () => {
    setErrores({});
    setPaso((p) => p - 1);
  };

  const siguientePaso2 = () => {
    if (!validarPaso2()) return;
    setErrores({});
    setPaso((p) => p + 1);
  };

  const siguientePaso3 = () => {
    if (!validarPaso3()) return;
    setErrores({});
    setPaso((p) => p + 1);
  };

  // ----- Envío final -----
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError("");
    setLoading(true);

    try {
      const fd = new FormData();

      // Paso 1
      fd.append("nombre", datos.nombre);
      fd.append("apellidos", datos.apellidos);
      fd.append("dni", datos.dni);
      fd.append("correo", datos.email); // backend espera "correo"
      fd.append("telefono", datos.telefono);
      fd.append("direccion", datos.direccion);
      fd.append("dni_file", datos.dniFile);
      fd.append("caducidad_dni", datos.dniCaducidad);

      // Paso 2
      fd.append("cap_file", docsPaso2.capFile);
      fd.append("caducidad_cap", docsPaso2.capCaducidad);
      fd.append("cap", "CAP"); // valor simbólico requerido

      fd.append("permiso_file", docsPaso2.permisoFile);
      fd.append("caducidad_permiso", docsPaso2.permisoCaducidad);
      fd.append("permiso_conduccion", "PERMISO"); // valor simbólico requerido

      fd.append("tarjeta_file", docsPaso2.tarjetaFile);
      fd.append("caducidad_tarjeta", docsPaso2.tarjetaCaducidad);
      fd.append("tarjeta_sanitaria", "TARJETA"); // valor simbólico requerido

      // Paso 3
      fd.append("cuenta_bancaria", datosBancarios.iban);
      fd.append("cuenta_file", datosBancarios.justificanteFile);
      // el backend requiere fecha → usamos hoy
      fd.append("caducidad_cuenta", new Date().toISOString().slice(0, 10));

      const res = await authFetch("/api/socios/alta", {
        method: "POST",
        body: fd, // no definas Content-Type manualmente
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `HTTP ${res.status}`);
      }

      const data = await res.json(); // { status, socio_id, matricula }
      onComplete?.(data);
    } catch (err) {
      setError(`Error al crear el socio: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md max-w-3xl mx-auto space-y-6">
      <h2 className="text-2xl font-bold text-center">Alta de nuevo socio</h2>

      {error && (
        <div className="p-3 rounded bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      {paso === 1 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Paso 1: Datos personales</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {["nombre", "apellidos"].map((campo) => (
              <div key={campo}>
                <input
                  type="text"
                  name={campo}
                  placeholder={campo.toUpperCase()}
                  value={datos[campo]}
                  onChange={handleChange}
                  className="w-full border p-2 rounded"
                />
                {errores[campo] && (
                  <p className="text-sm text-red-500">{errores[campo]}</p>
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <input
                type="text"
                name="dni"
                placeholder="DNI"
                value={datos.dni}
                onChange={handleChange}
                className="w-full border p-2 rounded"
              />
              {errores.dni && (
                <p className="text-sm text-red-500">{errores.dni}</p>
              )}
            </div>
            <div>
              <input
                type="email"
                name="email"
                placeholder="EMAIL"
                value={datos.email}
                onChange={handleChange}
                className="w-full border p-2 rounded"
              />
              {errores.email && (
                <p className="text-sm text-red-500">{errores.email}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <input
                type="tel"
                name="telefono"
                placeholder="TELÉFONO"
                value={datos.telefono}
                onChange={handleChange}
                className="w-full border p-2 rounded"
              />
              {errores.telefono && (
                <p className="text-sm text-red-500">{errores.telefono}</p>
              )}
            </div>
            <div>
              <input
                type="text"
                name="direccion"
                placeholder="DIRECCIÓN"
                value={datos.direccion}
                onChange={handleChange}
                className="w-full border p-2 rounded"
              />
              {errores.direccion && (
                <p className="text-sm text-red-500">{errores.direccion}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-600">Archivo DNI</label>
              <input type="file" name="dniFile" onChange={handleChange} />
              {errores.dniFile && (
                <p className="text-sm text-red-500">{errores.dniFile}</p>
              )}
            </div>
            <div>
              <label className="text-sm text-gray-600">Caducidad DNI</label>
              <input
                type="date"
                name="dniCaducidad"
                value={datos.dniCaducidad}
                onChange={handleChange}
                className="w-full border p-2 rounded"
              />
              {errores.dniCaducidad && (
                <p className="text-sm text-red-500">{errores.dniCaducidad}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={siguiente} className="bg-blue-600 text-white px-4 py-2 rounded">
              Siguiente
            </button>
          </div>
        </div>
      )}

      {paso === 2 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Paso 2: Documentación profesional</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-600">Archivo CAP</label>
              <input type="file" name="capFile" onChange={handlePaso2Change} />
              {errores.capFile && <p className="text-sm text-red-500">{errores.capFile}</p>}
            </div>
            <div>
              <label className="text-sm text-gray-600">Caducidad CAP</label>
              <input
                type="date"
                name="capCaducidad"
                value={docsPaso2.capCaducidad}
                onChange={handlePaso2Change}
                className="w-full border p-2 rounded"
              />
              {errores.capCaducidad && <p className="text-sm text-red-500">{errores.capCaducidad}</p>}
            </div>

            <div>
              <label className="text-sm text-gray-600">Permiso de conducción</label>
              <input type="file" name="permisoFile" onChange={handlePaso2Change} />
              {errores.permisoFile && <p className="text-sm text-red-500">{errores.permisoFile}</p>}
            </div>
            <div>
              <label className="text-sm text-gray-600">Caducidad permiso</label>
              <input
                type="date"
                name="permisoCaducidad"
                value={docsPaso2.permisoCaducidad}
                onChange={handlePaso2Change}
                className="w-full border p-2 rounded"
              />
              {errores.permisoCaducidad && <p className="text-sm text-red-500">{errores.permisoCaducidad}</p>}
            </div>

            <div>
              <label className="text-sm text-gray-600">Tarjeta sanitaria</label>
              <input type="file" name="tarjetaFile" onChange={handlePaso2Change} />
              {errores.tarjetaFile && <p className="text-sm text-red-500">{errores.tarjetaFile}</p>}
            </div>
            <div>
              <label className="text-sm text-gray-600">Caducidad tarjeta</label>
              <input
                type="date"
                name="tarjetaCaducidad"
                value={docsPaso2.tarjetaCaducidad}
                onChange={handlePaso2Change}
                className="w-full border p-2 rounded"
              />
              {errores.tarjetaCaducidad && <p className="text-sm text-red-500">{errores.tarjetaCaducidad}</p>}
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={anterior} className="border px-4 py-2 rounded">Atrás</button>
            <button onClick={siguientePaso2} className="bg-blue-600 text-white px-4 py-2 rounded">
              Siguiente
            </button>
          </div>
        </div>
      )}

      {paso === 3 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Paso 3: Datos bancarios</h3>

          <div>
            <input
              type="text"
              name="iban"
              placeholder="IBAN / Cuenta bancaria"
              value={datosBancarios.iban}
              onChange={handlePaso3Change}
              className="w-full border p-2 rounded"
            />
            {errores.iban && <p className="text-sm text-red-500">{errores.iban}</p>}
          </div>

          <div>
            <label className="text-sm text-gray-600">Justificante bancario</label>
            <input type="file" name="justificanteFile" onChange={handlePaso3Change} />
            {errores.justificanteFile && (
              <p className="text-sm text-red-500">{errores.justificanteFile}</p>
            )}
          </div>

          <div className="flex justify-between">
            <button onClick={anterior} className="border px-4 py-2 rounded">Atrás</button>
            <button onClick={siguientePaso3} className="bg-blue-600 text-white px-4 py-2 rounded">
              Siguiente
            </button>
          </div>
        </div>
      )}

      {paso === 4 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Paso 4: Confirmar alta</h3>
          <p>Revisa todos los datos y pulsa “Confirmar”.</p>

          <ul className="text-sm list-disc pl-5 space-y-1">
            <li>Nombre: {datos.nombre} {datos.apellidos}</li>
            <li>DNI: {datos.dni} (Caduca: {datos.dniCaducidad})</li>
            <li>Email: {datos.email}</li>
            <li>Teléfono: {datos.telefono}</li>
            <li>Dirección: {datos.direccion}</li>
            <li>IBAN: {datosBancarios.iban}</li>
            <li>Documentos: DNI, CAP, Permiso, Tarjeta, Justificante bancario</li>
          </ul>

          <div className="flex justify-between">
            <button onClick={anterior} className="border px-4 py-2 rounded">Atrás</button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-green-600 text-white px-4 py-2 rounded disabled:opacity-60"
            >
              {loading ? "Creando..." : "Confirmar alta"}
            </button>
          </div>
        </div>
      )}

      <div className="pt-6">
        <button onClick={onCancel} className="text-gray-600 underline text-sm">
          Cancelar
        </button>
      </div>
    </div>
  );
}
